import { useAgents } from "@/features/agents/hooks/use-agents";
import { useUser } from "@/features/auth/hooks/use-user";
import { useClientTeam } from "@/features/clients/hooks/use-clients";
import {
  useConnectors,
  useHeyupNumbers,
} from "@/features/connectors/hooks/use-connectors";
import type {
  CallDestination,
  CallRoutingRule,
  Connector,
  ConnectorCapability,
  ConnectorStatus,
  ConnectorType,
  GeoCallFilter,
  OutboundCallFilter,
} from "@/features/connectors/types";
import {
  CONNECTOR_CAPABILITIES,
  CONNECTOR_TYPES,
  defaultGeoFilter,
  defaultOutboundFilter,
} from "@/features/connectors/types";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FORM_CHANNEL_TYPES, type FormChannel } from "../channels";
import type { RoutableUser, TransferableMember } from "../destination-list";
import {
  emptyEmailAccountForm,
  emptySipRegistrationForm,
  type EmailAccountForm,
  type MailServerForm,
  type SipRegistrationForm,
} from "./types";

/**
 * Read a connector's fallback into the ordered list the form edits.
 *
 * Three generations of configuration, newest first — and the two older ones are
 * promoted as `always`, never gated on a schedule: they were configured when a
 * fallback rang whatever the hour, and quietly adding a window to them on the
 * next save would take a working safety net away from whoever set it up.
 */
/** The backend's cap (MAX_RING_BEFORE_AGENT_S): past it the caller has hung up. */
export const RING_BEFORE_AGENT_MAX_S = 120;

export const readFallbackDestinations = (
  connector?: Connector,
): CallDestination[] => {
  const config = connector?.configuration;
  if (config?.fallback_destinations?.length)
    return config.fallback_destinations;
  if (config?.fallback_destination) return [config.fallback_destination];
  if (config?.fallback_member_id)
    return [{ kind: "member", member_id: config.fallback_member_id }];
  if (config?.fallback_number)
    return [{ kind: "phone", number: config.fallback_number }];
  return [];
};

/**
 * Read a transfer rule's destinations, promoting the legacy member/number pair
 * the same way.
 */
export const readTransferDestinations = (
  rule: CallRoutingRule,
): CallDestination[] => {
  if (rule.transfer_destinations?.length) return rule.transfer_destinations;
  if (rule.transfer_member_id)
    return [{ kind: "member", member_id: rule.transfer_member_id }];
  if (rule.transfer_number?.trim())
    return [{ kind: "phone", number: rule.transfer_number.trim() }];
  return [];
};

const newRoutingRule = (): CallRoutingRule => ({
  id:
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `rule-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  pattern: "",
  action: "agent",
});

/**
 * What a type ticks by default lives on the type table, next to what it may
 * declare at all — the backend reads the same field when the payload names no
 * capability (CONNECTOR_TYPES[type].defaultCapabilities). Copied: this goes
 * into form state, and the table is shared by the whole app.
 */
const getDefaultCapabilities = (
  connectorType: ConnectorType,
): ConnectorCapability[] => [
  ...CONNECTOR_TYPES[connectorType].defaultCapabilities,
];

/**
 * Every field the connector form edits, the lists it offers, and what the
 * current user is allowed to see. The form itself only composes sections over
 * this.
 */
/** The stored registration as form fields; the password is never sent to us. */
const readSipRegistration = (connector?: Connector): SipRegistrationForm => {
  const stored = connector?.configuration?.sip_registration;
  if (!stored) return emptySipRegistrationForm();
  return {
    server: stored.server || "",
    port: stored.port ? String(stored.port) : "",
    transport: stored.transport || "udp",
    username: stored.username || "",
    authUsername: stored.auth_username || "",
    password: "",
    outboundPrefix: stored.outbound_prefix || "",
    numberFormat: stored.number_format || "national",
  };
};

/** The stored mailbox as form fields; the passwords are never sent to us. */
const readEmailAccount = (connector?: Connector): EmailAccountForm => {
  const stored = connector?.configuration?.email_account;
  if (!stored) return emptyEmailAccountForm();
  const server = (side: typeof stored.smtp): MailServerForm => ({
    host: side.host || "",
    port: side.port ? String(side.port) : "",
    secure: side.secure ?? true,
    // The login that is the address itself is shown empty: that is the default.
    username: side.username === stored.address ? "" : side.username || "",
    password: "",
  });
  return {
    address: stored.address || "",
    displayName: stored.display_name || "",
    smtp: server(stored.smtp),
    imap: server(stored.imap),
    mailbox: stored.imap.mailbox || "",
    saveToSent: stored.save_to_sent ?? null,
    sentFolder: stored.sent_folder || "",
  };
};

export function useConnectorFormState(
  connector?: Connector,
  channel?: FormChannel,
) {
  const { company: clientId } = useParams<{ company: string }>();
  const { data: agents = [] } = useAgents();
  const { data: team } = useClientTeam(clientId || "");
  const members = team?.members || [];
  const { data: user } = useUser();
  const { data: existingConnectors = [] } = useConnectors(clientId || "");
  // The numbers HeyUp attributed to this account. A "Numéro HeyUp" channel is a
  // selector over these and nothing else: the line runs on our carrier account,
  // so a number the client typed in would be someone else's to answer.
  const { data: heyupNumbers } = useHeyupNumbers(clientId || "");
  const attributedNumbers = heyupNumbers?.numbers || [];
  // Offered only where it can work: a deployment with no HeyUp carrier account,
  // or an account we have not attributed a number to yet, has nothing to pick.
  const canUseHeyupNumber =
    Boolean(heyupNumbers?.enabled) && attributedNumbers.length > 0;
  // One demo connector per account: it is picked by tenant, not by number, so a
  // second one could serve a call with the wrong agent. The server enforces it;
  // hide the option rather than let the user hit that error.
  const hasTemporaryConnector = existingConnectors.some(
    (c) => c.type === "temporary" && c.id !== connector?.id,
  );
  // Flagging a number as a shared demo number is platform staff only: it opens
  // that number to calls from other tenants.
  const isSysadmin = user?.role === "SYSADMIN";

  // Team members a call can be sent to. Any route counts: someone who answers
  // on a VoIP handset, or on an extension of the client's own PBX, may have no
  // mobile on file at all, and excluding them here would make that phone
  // unusable for routing.
  //
  // Keyed on the member id, and no longer requiring an attached account: a
  // performer synced from the booking provider, or anyone added to the team
  // without an invitation, has no login and was silently unroutable.
  const transferableMembers: TransferableMember[] = members
    .filter(
      (m) =>
        m.membership?.phone ||
        m.membership?.device_id ||
        m.membership?.sip_extension,
    )
    .map((m) => ({
      id: m.membership.id,
      phone: m.membership.phone,
      onDevice: !!m.membership.device_id,
      name:
        m.membership.display_name ||
        m.user?.full_name ||
        m.user?.email ||
        m.membership.phone ||
        "",
    }));

  // Logins, on the phone of their own account. A different list and a different
  // id space from the one above — the same person can appear in both, and which
  // one a rule names decides which number rings.
  const routableUsers: RoutableUser[] = (team?.accesses || [])
    .filter((entry) => entry.user?.phone)
    .map((entry) => ({
      id: entry.user!.id,
      phone: entry.user!.phone,
      name: entry.user!.full_name || entry.user!.email || "",
    }));

  // A new connector defaults to `heyup` — one of our numbers, the common case —
  // for staff as well: `twilio` is an admin exception now, not a starting point.
  const [type, setType] = useState<ConnectorType>(
    connector?.type || (channel ? FORM_CHANNEL_TYPES[channel][0] : "heyup"),
  );
  // A phone line starts on a HeyUp number — but only for a client who has one to
  // pick; the others start on their own PBX rather than on an empty selector.
  useEffect(() => {
    if (
      !connector &&
      channel === "phone" &&
      heyupNumbers &&
      !canUseHeyupNumber &&
      type === "heyup"
    ) {
      setType("sip");
    }
  }, [heyupNumbers, canUseHeyupNumber]);
  const [status, setStatus] = useState<ConnectorStatus>(
    connector?.status || "active",
  );
  const [agentId, setAgentId] = useState<string>(connector?.agent_id || "");
  const [capabilities, setCapabilities] = useState<ConnectorCapability[]>(
    connector?.capabilities || getDefaultCapabilities(type),
  );

  // Twilio fields
  const [accountId, setAccountId] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [twilioRegion, setTwilioRegion] = useState("");
  const [phoneNumbers, setPhoneNumbers] = useState("");
  const [whatsappNumbers, setWhatsappNumbers] = useState("");
  const [routingRules, setRoutingRules] = useState<CallRoutingRule[]>(
    connector?.configuration?.routing_rules || [],
  );
  const [outboundFilter, setOutboundFilter] = useState<OutboundCallFilter>(
    connector?.configuration?.outbound_filter || defaultOutboundFilter(),
  );
  const [geoFilter, setGeoFilter] = useState<GeoCallFilter>(
    connector?.configuration?.geo_filter || defaultGeoFilter(),
  );
  const [fallbackDestinations, setFallbackDestinations] = useState<
    CallDestination[]
  >(() => readFallbackDestinations(connector));
  // Kept apart from the schedule id so ticking the box before picking a
  // schedule is a state the form can hold (and refuse on submit).
  const [agentOffEnabled, setAgentOffEnabled] = useState(
    !!connector?.configuration?.agent_off_schedule_id,
  );
  const [agentOffScheduleId, setAgentOffScheduleId] = useState(
    connector?.configuration?.agent_off_schedule_id || "",
  );
  const [ringBeforeAgentSeconds, setRingBeforeAgentSeconds] = useState(
    connector?.configuration?.ring_before_agent_seconds || 0,
  );
  const [allowedOrigins, setAllowedOrigins] = useState(
    (connector?.configuration?.allowed_origins || []).join(", "),
  );
  const [callerNumbers, setCallerNumbers] = useState(
    (connector?.configuration?.caller_numbers || []).join(", "),
  );
  const [demoNumber, setDemoNumber] = useState(
    connector?.configuration?.demo_number || false,
  );
  const [codeRouting, setCodeRouting] = useState(
    connector?.configuration?.code_routing || false,
  );
  const [defaultConnector, setDefaultConnector] = useState(
    connector?.configuration?.default_connector || false,
  );
  const [publicCallbackEnabled, setPublicCallbackEnabled] = useState(
    connector?.configuration?.public_callback_enabled === true,
  );
  const [webCallEnabled, setWebCallEnabled] = useState(
    connector?.configuration?.web_call_enabled === true,
  );
  const [sipRegistration, setSipRegistration] = useState<SipRegistrationForm>(
    () => readSipRegistration(connector),
  );
  const updateSipRegistration = (changes: Partial<SipRegistrationForm>) =>
    setSipRegistration((prev) => ({ ...prev, ...changes }));

  const [emailAccount, setEmailAccount] = useState<EmailAccountForm>(() =>
    readEmailAccount(connector),
  );
  const updateEmailAccount = (changes: Partial<EmailAccountForm>) =>
    setEmailAccount((prev) => ({ ...prev, ...changes }));

  // Initialize form fields when connector changes (edit mode)
  useEffect(() => {
    if (connector) {
      setType(connector.type);
      setStatus(connector.status);
      setAgentId(connector.agent_id || "");
      setCapabilities(
        connector.capabilities || getDefaultCapabilities(connector.type),
      );
      setAccountId(connector.configuration?.account_id || "");
      setAuthToken(""); // Don't show existing token for security
      setTwilioRegion(connector.configuration?.twilio_region || "");
      setPhoneNumbers(connector.configuration?.phone_numbers?.join(", ") || "");
      setWhatsappNumbers(
        connector.configuration?.whatsapp_numbers?.join(", ") || "",
      );
      setRoutingRules(connector.configuration?.routing_rules || []);
      setGeoFilter(connector.configuration?.geo_filter || defaultGeoFilter());
      setOutboundFilter(
        connector.configuration?.outbound_filter || defaultOutboundFilter(),
      );
      setFallbackDestinations(readFallbackDestinations(connector));
      setAgentOffEnabled(!!connector.configuration?.agent_off_schedule_id);
      setAgentOffScheduleId(
        connector.configuration?.agent_off_schedule_id || "",
      );
      setRingBeforeAgentSeconds(
        connector.configuration?.ring_before_agent_seconds || 0,
      );
      setAllowedOrigins(
        (connector.configuration?.allowed_origins || []).join(", "),
      );
      setCallerNumbers(
        (connector.configuration?.caller_numbers || []).join(", "),
      );
      setDemoNumber(connector.configuration?.demo_number || false);
      setCodeRouting(connector.configuration?.code_routing || false);
      setDefaultConnector(connector.configuration?.default_connector || false);
      setWebCallEnabled(connector.configuration?.web_call_enabled === true);
      setSipRegistration(readSipRegistration(connector));
      setEmailAccount(readEmailAccount(connector));
      setPublicCallbackEnabled(
        connector.configuration?.public_callback_enabled === true,
      );
    }
  }, [connector]);

  // Adjust capabilities when connector type changes
  useEffect(() => {
    if (!connector) {
      // Only auto-adjust in create mode
      const availableCapabilities = CONNECTOR_CAPABILITIES[type];
      // Filter out capabilities that are not available for the new type
      const filteredCapabilities = capabilities.filter((cap) =>
        availableCapabilities.includes(cap),
      );
      // If no capabilities remain, set default ones
      if (filteredCapabilities.length === 0) {
        setCapabilities(getDefaultCapabilities(type));
      } else {
        setCapabilities(filteredCapabilities);
      }
    }
  }, [type]);

  const updateRoutingRule = (id: string, changes: Partial<CallRoutingRule>) => {
    setRoutingRules((prev) =>
      prev.map((rule) => (rule.id === id ? { ...rule, ...changes } : rule)),
    );
  };

  const addRoutingRule = () => {
    setRoutingRules((prev) => [...prev, newRoutingRule()]);
  };

  const removeRoutingRule = (id: string) => {
    setRoutingRules((prev) => prev.filter((rule) => rule.id !== id));
  };

  const toggleCapability = (capability: ConnectorCapability) => {
    setCapabilities((prev) =>
      prev.includes(capability)
        ? prev.filter((c) => c !== capability)
        : [...prev, capability],
    );
  };

  // `phoneNumbers` is the comma-separated string every type shares; a HeyUp
  // channel edits it as a set of tick-boxes over the numbers we attributed.
  const selectedPhoneNumbers = phoneNumbers
    .split(",")
    .map((number) => number.trim())
    .filter(Boolean);

  const heyupSelectableNumbers = [
    ...new Set([...attributedNumbers, ...selectedPhoneNumbers]),
  ];

  const toggleHeyupNumber = (number: string) => {
    const next = selectedPhoneNumbers.includes(number)
      ? selectedPhoneNumbers.filter((claimed) => claimed !== number)
      : [...selectedPhoneNumbers, number];
    setPhoneNumbers(next.join(", "));
  };

  return {
    clientId,
    agents,
    isSysadmin,
    canUseHeyupNumber,
    hasTemporaryConnector,
    transferableMembers,
    routableUsers,
    selectedPhoneNumbers,
    heyupSelectableNumbers,
    toggleHeyupNumber,
    toggleCapability,
    updateRoutingRule,
    addRoutingRule,
    removeRoutingRule,

    type,
    setType,
    status,
    setStatus,
    agentId,
    setAgentId,
    capabilities,
    setCapabilities,
    accountId,
    setAccountId,
    authToken,
    setAuthToken,
    twilioRegion,
    setTwilioRegion,
    phoneNumbers,
    setPhoneNumbers,
    sipRegistration,
    updateSipRegistration,
    emailAccount,
    updateEmailAccount,
    whatsappNumbers,
    setWhatsappNumbers,
    routingRules,
    outboundFilter,
    setOutboundFilter,
    geoFilter,
    setGeoFilter,
    fallbackDestinations,
    setFallbackDestinations,
    agentOffEnabled,
    setAgentOffEnabled,
    agentOffScheduleId,
    setAgentOffScheduleId,
    ringBeforeAgentSeconds,
    setRingBeforeAgentSeconds,
    allowedOrigins,
    setAllowedOrigins,
    callerNumbers,
    setCallerNumbers,
    demoNumber,
    setDemoNumber,
    codeRouting,
    setCodeRouting,
    defaultConnector,
    setDefaultConnector,
    publicCallbackEnabled,
    setPublicCallbackEnabled,
    webCallEnabled,
    setWebCallEnabled,
  };
}

export type ConnectorFormState = ReturnType<typeof useConnectorFormState>;
